import { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import PublicLayout from '@/components/layouts/PublicLayout';
import AuthLayout from '@/components/layouts/AuthLayout';
import MainLayout from '@/components/layouts/MainLayout';

// 加载指示器
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
);

// ===== v2 8 屏数据展示首页（一级首页 /）=====
const LandingHome = lazy(() => import('@/landing/HomePage'));
// ===== v2 客群轻量页（/audience/:slug）=====
const AudiencePage = lazy(() => import('@/landing/AudiencePage'));

// ===== v1 原智能平台走马灯（二级首页 /home）=====
const LegacyHomePage = lazy(() => import('@/pages/public/LegacyHomePage'));

// 使用React.lazy进行路由懒加载
const ProjectIntroPage = lazy(() => import('@/pages/public/ProjectIntroPage'));
const SearchResultsPage = lazy(() => import('@/pages/public/SearchResultsPage'));
const DatasetDetailPage = lazy(() => import('@/pages/public/DatasetDetailPage'));
const MyDataCenter = lazy(() => import('@/pages/public/MyDataCenter'));
const FilterDemoPage = lazy(() => import('@/pages/public/FilterDemoPage'));
const DataServiceApplication = lazy(() => import('@/pages/public/DataServiceApplication'));
const UserProfile = lazy(() => import('@/pages/public/UserProfile'));
const PrivacyPolicy = lazy(() => import('@/pages/public/PrivacyPolicy'));
const AboutUs = lazy(() => import('@/pages/public/AboutUs'));
const ContactUs = lazy(() => import('@/pages/public/ContactUs'));
const HelpCenter = lazy(() => import('@/pages/public/HelpCenter'));
const CatalogPage = lazy(() => import('@/pages/Catalog'));
const DataProvidersPage = lazy(() => import('@/pages/public/DataProvidersPage'));
const PricingPage = lazy(() => import('@/pages/public/PricingPage'));
const CaseStudiesPage = lazy(() => import('@/pages/public/CaseStudiesPage'));
const NotFound = lazy(() => import('@/pages/public/NotFound'));

// 认证页面懒加载
const Login = lazy(() => import('@/pages/auth/Login'));
const Register = lazy(() => import('@/pages/auth/Register'));
const AuthUnified = lazy(() => import('@/pages/auth/AuthUnified'));
const CallbackPage = lazy(() => import('@/pages/auth/CallbackPage'));

// 订单页面懒加载
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/orders/OrderDetailPage'));

// 新功能页面懒加载（收藏/密钥/定制）
const MyFavorites = lazy(() => import('@/pages/profile/MyFavorites'));
const AccessKeys = lazy(() => import('@/pages/profile/AccessKeys'));
const MyCustomRequests = lazy(() => import('@/pages/profile/MyCustomRequests'));
const CustomDataRequest = lazy(() => import('@/pages/public/CustomDataRequest'));

/**
 * 一级首页（v2 8屏数据展示站）与客群轻量页
 * 独立顶层路由，不套 PublicLayout（v2 页面自带 Navbar/Footer 全屏深色展示）
 */
export const landingRoutes: RouteObject[] = [
  {
    path: '/',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LandingHome />
      </Suspense>
    ),
  },
  {
    path: '/audience/:slug',
    element: (
      <Suspense fallback={<PageLoader />}>
        <AudiencePage />
      </Suspense>
    ),
  },
];

/**
 * 公开路由 - 无需登录即可访问
 * 注意：一级首页 `/` 已由 landingRoutes 接管，这里不再定义 index
 */
export const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { path: 'home', element: <Suspense fallback={<PageLoader />}><LegacyHomePage /></Suspense> },
      { path: 'intro', element: <Suspense fallback={<PageLoader />}><ProjectIntroPage /></Suspense> },
      { path: 'project-intro', element: <Suspense fallback={<PageLoader />}><ProjectIntroPage /></Suspense> },
      { path: 'search', element: <Suspense fallback={<PageLoader />}><SearchResultsPage /></Suspense> },
      { path: 'dataset/:id', element: <Suspense fallback={<PageLoader />}><DatasetDetailPage /></Suspense> },
      { path: 'catalog', element: <Suspense fallback={<PageLoader />}><CatalogPage /></Suspense> },
      { path: 'providers', element: <Suspense fallback={<PageLoader />}><DataProvidersPage /></Suspense> },
      { path: 'pricing', element: <Suspense fallback={<PageLoader />}><PricingPage /></Suspense> },
      { path: 'cases', element: <Suspense fallback={<PageLoader />}><CaseStudiesPage /></Suspense> },
      { path: 'filter-demo', element: <Suspense fallback={<PageLoader />}><FilterDemoPage /></Suspense> },
      { path: 'privacy', element: <Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense> },
      { path: 'about', element: <Suspense fallback={<PageLoader />}><AboutUs /></Suspense> },
      { path: 'contact', element: <Suspense fallback={<PageLoader />}><ContactUs /></Suspense> },
      { path: 'help', element: <Suspense fallback={<PageLoader />}><HelpCenter /></Suspense> },
    ],
  },
];

/**
 * 受保护路由 - 需要登录才能访问
 * 包括：我的数据中心、用户资料、数据服务申请
 */
export const protectedRoutes: RouteObject[] = [
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      {
        path: 'my-data',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <MyDataCenter />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'apply',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <DataServiceApplication />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'apply/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <DataServiceApplication />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'orders',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'orders/:id',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'profile/favorites',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <MyFavorites />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'profile/access-keys',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <AccessKeys />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'profile/my-requests',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <MyCustomRequests />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'custom-data-request',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProtectedRoute>
              <CustomDataRequest />
            </ProtectedRoute>
          </Suspense>
        )
      },
    ],
  },
];

/**
 * 认证路由 - 登录、注册、回调页面
 */
export const authRoutes: RouteObject[] = [
  {
    path: '/auth',
    element: <PublicLayout />,
    children: [
      { path: 'login', element: <Suspense fallback={<PageLoader />}><Login /></Suspense> },
      { path: 'register', element: <Suspense fallback={<PageLoader />}><Register /></Suspense> },
      { path: 'unified', element: <Suspense fallback={<PageLoader />}><AuthUnified /></Suspense> },
      { path: 'callback', element: <Suspense fallback={<PageLoader />}><CallbackPage /></Suspense> },
      // 默认重定向到统一认证页面
      { path: '', element: <Navigate to="unified" replace /> },
    ],
  },
];

/**
 * 404 路由 - 必须放在最后
 */
export const notFoundRoutes: RouteObject[] = [
  { path: '*', element: <Suspense fallback={<PageLoader />}><NotFound /></Suspense> },
];

/**
 * 合并所有路由
 */
export const routes: RouteObject[] = [
  ...landingRoutes,
  ...publicRoutes,
  ...protectedRoutes,
  ...authRoutes,
  ...notFoundRoutes,
];

export default routes;
